import { createClient } from '../client/browser'
import type { SupabaseClient } from '@supabase/supabase-js'

export interface MatchingCriteria {
  sessionType: 'individual' | 'couples'
  therapistTier: 'resident' | 'associate'
  rate: number
  timezone: string
  availability: string[]
  insurance: string[]
  province?: string
}

export interface FindMatchingProsOptions {
  /**
   * When true, if fewer than 4 internal pros match we pad the result with
   * Between-Sessions therapists who accept new clients in the caller's
   * province. Client app only — employee/imwell/mhap flows must leave this
   * off, since BES pros are external practices not covered by those programs.
   */
  includeBesFallback?: boolean
}

const TARGET_MATCH_COUNT = 4
const BES_FAIRNESS_WINDOW_DAYS = 30

/**
 * Find matching pros based on quiz criteria.
 * Returns internal Alli pros as `proIds`; when `includeBesFallback` is set
 * and internal < TARGET_MATCH_COUNT, the remainder is filled with BES pros
 * as `besProIds` + per-pro `besMatchReasons`.
 */
export async function findMatchingPros(
  criteria: MatchingCriteria,
  supabase?: SupabaseClient,
  opts: FindMatchingProsOptions = {},
): Promise<{
  proIds: string[]
  besProIds: string[]
  besMatchReasons: Record<string, string[]>
  error?: string
}> {
  const client = supabase || createClient()

  try {
    // Start with base query for active therapists
    let query = client
      .from('pros')
      .select(`
        id,
        timezone,
        schedule,
        credentials,
        matched
      `)
      .eq('isActive', true)
      .eq('isTherapist', true)
    
    // Filter by session type - only fetch pros who accept this type
    if (criteria.sessionType === 'couples') {
      query = query.eq('isAcceptingCouples', true)
    } else {
      query = query.eq('isAcceptingIndividual', true)
    }
    
    // Filter by therapist tier
    if (criteria.therapistTier === 'resident') {
      // Resident tier MUST be RESIDENT employment status. Cohorts open
      // automatically once their startDate has passed — no code edit
      // needed per cohort. Admin can still gate individual pros (or an
      // entire cohort in bulk) via the isAcceptingIndividual/Couples
      // flags managed at /admin/cohorts.
      const today = new Date().toISOString().slice(0, 10)
      query = query.eq('employmentStatus', 'RESIDENT').lte('startDate', today)
    } else {
      // Associate tier - can be EMPLOYEE or CONTRACTOR
      // Check if ONLY RSW insurance is selected
      const hasOnlyRSWInsurance = criteria.insurance.length === 1 && criteria.insurance[0] === 'rsw'
      
      if (hasOnlyRSWInsurance) {
        // ONLY RSW insurance requires CONTRACTOR with RSW credentials
        query = query.eq('employmentStatus', 'CONTRACTOR')
      } else {
        // All other insurance combinations - EMPLOYEE only
        query = query.eq('employmentStatus', 'EMPLOYEE')
      }
    }
    
    // Execute query
    console.log('Executing pro query...')
    const { data: pros, error } = await query
    
    if (error) {
      console.error('Error finding matching pros:', error)
      return { proIds: [], besProIds: [], besMatchReasons: {}, error: 'Failed to find matching therapists' }
    }

    console.log('Found pros:', pros?.length || 0)

    if (!pros || pros.length === 0) {
      // Internal pool empty — see if BES can still fill.
      const bes = opts.includeBesFallback
        ? await findBesPros(criteria, TARGET_MATCH_COUNT, client)
        : { besProIds: [], besMatchReasons: {} }
      if (bes.besProIds.length === 0) {
        return { proIds: [], besProIds: [], besMatchReasons: {}, error: 'No matching therapists found' }
      }
      return { proIds: [], besProIds: bes.besProIds, besMatchReasons: bes.besMatchReasons }
    }
    
    // Additional filtering for RSW credentials if needed
    let filteredPros = pros
    if (criteria.therapistTier === 'associate' && 
        criteria.insurance.length === 1 && 
        criteria.insurance[0] === 'rsw') {
      filteredPros = pros.filter(pro => {
        if (!pro.credentials || typeof pro.credentials !== 'object') return false
        // Check if credentials contain RSW
        const credentialsStr = JSON.stringify(pro.credentials).toLowerCase()
        return credentialsStr.includes('rsw') || credentialsStr.includes('registered social worker')
      })
    }
    
    // Score and sort pros based on matching criteria
    const scoredPros = filteredPros.map(pro => {
      let score = 0
      
      // Availability match with timezone adjustment
      if (pro.schedule) {
        const availabilityScore = calculateAvailabilityMatch(
          pro.schedule,
          criteria.availability,
          criteria.timezone,
          pro.timezone
        )
        score += availabilityScore
      }
      
      // Fairness score - strongly prioritize pros who have been matched less
      // Use exponential decay to heavily penalize over-matched therapists
      const matchedCount = pro.matched || 0
      let fairnessScore = 0
      
      if (matchedCount < 50) {
        fairnessScore = 100 // Huge bonus for new therapists
      } else if (matchedCount < 100) {
        fairnessScore = 50 // Good bonus for less-matched therapists
      } else if (matchedCount < 150) {
        fairnessScore = 20 // Small bonus
      } else if (matchedCount < 200) {
        fairnessScore = 0 // Neutral
      } else {
        // Exponential penalty for over-matched therapists
        fairnessScore = -Math.min(100, (matchedCount - 200) * 0.5)
      }
      
      score += fairnessScore
      
      return { 
        id: pro.id,
        score,
        matchedCount
      }
    })
    
    // Add small random factor to scores to break ties and distribute matches
    const prosWithRandomization = scoredPros.map(pro => ({
      ...pro,
      // Add random value between -5 and 5 to help distribute similar scores
      finalScore: pro.score + (Math.random() * 10 - 5)
    }))
    
    // Sort by final score (highest first) and limit to top 4 matches
    const topMatches = prosWithRandomization
      .sort((a, b) => b.finalScore - a.finalScore)
      .slice(0, 4) // Return top 4 matches (or fewer if less than 4 available)
    
    // Log matching details for debugging
    console.log(`Top ${topMatches.length} matches:`, topMatches.map(m => ({
      id: m.id,
      matchedCount: m.matchedCount,
      score: m.score,
      finalScore: m.finalScore.toFixed(2)
    })))
    
    const sortedProIds = topMatches.map(pro => pro.id) // Extract just the IDs

    // Top up with BES pros if the caller opted in and we came up short.
    const shortfall = TARGET_MATCH_COUNT - sortedProIds.length
    const bes = opts.includeBesFallback && shortfall > 0
      ? await findBesPros(criteria, shortfall, client)
      : { besProIds: [], besMatchReasons: {} }

    return {
      proIds: sortedProIds,
      besProIds: bes.besProIds,
      besMatchReasons: bes.besMatchReasons,
    }

  } catch (error) {
    console.error('Unexpected error in findMatchingPros:', error)
    return { proIds: [], besProIds: [], besMatchReasons: {}, error: 'An unexpected error occurred' }
  }
}

/**
 * Find BES pros matching the client's criteria to top up an under-4 match set.
 * Hard filters: employmentStatus=BES, active, onboarded, directory-visible,
 * bsExternalPractice.acceptingNewClients=true, sessionType available,
 * client's province in serviceProvinces. Ordered by BES-scoped fairness
 * (recent bes_referrals in last 30 days ASC), random tiebreak.
 */
async function findBesPros(
  criteria: MatchingCriteria,
  needed: number,
  client: SupabaseClient,
): Promise<{ besProIds: string[]; besMatchReasons: Record<string, string[]> }> {
  if (!criteria.province || needed <= 0) {
    return { besProIds: [], besMatchReasons: {} }
  }

  // Session availability uses the top-level pros booleans (isAcceptingIndividual /
  // isAcceptingCouples). The profile-builder syncs these from BOTH the legacy
  // bsExternalPractice.services shape and the new bsPractice.clients.accepting[]
  // array, so this is the only signal that survives across old + new profiles.
  const sessionAcceptCol = criteria.sessionType === 'couples' ? 'isAcceptingCouples' : 'isAcceptingIndividual'

  // Province filter — only reads bsPractice.details.provincesServed (what the
  // current profile-builder writes). Legacy bsExternalPractice.serviceProvinces
  // is deliberately NOT considered; a pro with only-legacy province data must
  // re-open the profile-builder to be matched.
  const { data: candidates, error } = await client
    .from('pros')
    .select('id, bsExternalPractice, bsPractice')
    .eq('employmentStatus', 'BES')
    .eq('isActive', true)
    .is('deletedAt', null)
    .not('bsOnboardedAt', 'is', null)
    .not('directoryHidden', 'is', true)
    .eq(sessionAcceptCol, true)

  if (error) {
    console.error('Error querying BES fallback pros:', error)
    return { besProIds: [], besMatchReasons: {} }
  }

  if (!candidates || candidates.length === 0) {
    return { besProIds: [], besMatchReasons: {} }
  }

  // Province filter — only bsPractice.details.provincesServed (the current
  // profile-builder field). Legacy bsExternalPractice.serviceProvinces is
  // intentionally ignored.
  const province = criteria.province
  const provinceMatched = candidates.filter(pro => {
    const bp = pro.bsPractice as { details?: { provincesServed?: string[] } } | null
    return (bp?.details?.provincesServed ?? []).includes(province)
  })

  if (provinceMatched.length === 0) {
    return { besProIds: [], besMatchReasons: {} }
  }

  const ids = provinceMatched.map(p => p.id)
  const since = new Date(Date.now() - BES_FAIRNESS_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString()

  // Fairness: count how many quizzes have surfaced each candidate in the
  // window, then prefer the least-surfaced. Reads matching_quizzes.besProIds
  // (the source of truth for algorithmic surfacings), not bes_referrals —
  // the latter only holds real inquiries under the new model, so it'd
  // massively underweight the rotation.
  const { data: recentQuizzes } = await client
    .from('matching_quizzes')
    .select('besProIds')
    .overlaps('besProIds', ids)
    .gte('createdAt', since)
    .is('deletedAt', null)

  const recentCount = new Map<string, number>()
  for (const row of recentQuizzes ?? []) {
    const surfaced = ((row as { besProIds: string[] | null }).besProIds) ?? []
    for (const proId of surfaced) {
      if (recentCount.has(proId) || ids.includes(proId)) {
        recentCount.set(proId, (recentCount.get(proId) ?? 0) + 1)
      }
    }
  }

  const scored = provinceMatched.map(pro => ({
    id: pro.id,
    recent: recentCount.get(pro.id) ?? 0,
    reasons: buildBesMatchReasons(pro, criteria),
    jitter: Math.random(),
  }))

  scored.sort((a, b) => a.recent - b.recent || a.jitter - b.jitter)

  const top = scored.slice(0, needed)
  return {
    besProIds: top.map(p => p.id),
    besMatchReasons: Object.fromEntries(top.map(p => [p.id, p.reasons])),
  }
}

function buildBesMatchReasons(
  pro: { bsPractice?: unknown },
  criteria: MatchingCriteria,
): string[] {
  const reasons: string[] = []
  // Only reads from bsPractice (what the current profile-builder writes).
  const bp = (pro.bsPractice ?? {}) as {
    details?: { provincesServed?: string[] }
    clients?: {
      accepting?: string[]
      pricing?: Record<string, { rate?: number; slidingScaleEnabled?: boolean; slidingScaleMin?: number | null }>
    }
  }

  if (criteria.province && bp.details?.provincesServed?.includes(criteria.province)) {
    reasons.push(`Serves ${criteria.province}`)
  }

  if (bp.clients?.accepting?.includes(criteria.sessionType)) {
    reasons.push(criteria.sessionType === 'couples' ? 'Couples therapy available' : 'Individual therapy available')
  }

  const pricing = bp.clients?.pricing?.[criteria.sessionType]
  const hasSlidingScale =
    pricing?.slidingScaleEnabled === true ||
    (pricing?.slidingScaleMin ?? null) !== null
  if (hasSlidingScale) reasons.push('Offers sliding scale')

  return reasons
}

/**
 * Record BES pros surfaced for a matching quiz on the quiz row itself.
 * We do NOT create bes_referrals rows at match time — most surfacings
 * never lead to an inquiry, so per-surfacing rows just duplicated client
 * PII for referrals that would never exist. A bes_referrals row is only
 * created when the client actually submits an inquiry (see
 * apps/client/app/api/client/bes-referrals/request-consult/route.ts and
 * .../matching-quizzes/[quizId]/inquire/route.ts).
 *
 * Metrics/tracking rely on matching_quizzes.besProIds — a therapist's
 * total surfacings = quizzes where their id is ANY(besProIds).
 */
export async function updateMatchingQuizWithBesPros(
  quizId: string,
  besProIds: string[],
  besMatchReasons: Record<string, string[]>,
  supabase?: SupabaseClient,
): Promise<{ success: boolean; error?: string }> {
  const client = supabase || createClient()
  if (besProIds.length === 0) return { success: true }

  const { error } = await client
    .from('matching_quizzes')
    .update({
      besProIds,
      // Persisted so request-consult can hydrate criteria.matchReasons on
      // the bes_referrals row (the therapist inquiry email + admin/inbox
      // views read the reasons from there).
      besMatchReasons,
      updatedAt: new Date().toISOString(),
    })
    .eq('id', quizId)

  if (error) {
    console.error('[matching_quizzes] besProIds update failed:', error)
    return { success: false, error: error.message }
  }
  return { success: true }
}

/**
 * Calculate availability match score with timezone adjustment
 */
function calculateAvailabilityMatch(
  proSchedule: any,
  userPreferences: string[],
  userTimezone: string,
  proTimezone: string | null
): number {
  let score = 0
  
  if (!proSchedule || typeof proSchedule !== 'object') {
    return 0
  }
  
  // Count total availability slots to detect "all available" therapists
  const totalSlots = Object.keys(proSchedule).filter(k => proSchedule[k] === true).length
  const hasAllSlotsAvailable = totalSlots >= 7 // 7 days = likely default data
  
  // Get timezone offset difference in hours
  const timezoneOffsetHours = getTimezoneOffsetHours(userTimezone, proTimezone)
  
  // Check each user preference against pro's schedule
  userPreferences.forEach(preference => {
    // Handle special case for 'flexible' availability
    if (preference === 'flexible') {
      // Give a moderate score for flexible availability
      score += 3
      return
    }
    
    const parts = preference.split('-')
    if (parts.length !== 2) {
      console.warn(`Invalid availability preference format: ${preference}`)
      return
    }
    
    const [dayType, timeOfDay] = parts
    
    // Adjust time of day based on timezone difference
    const adjustedTimeOfDay = timeOfDay ? adjustTimeOfDay(timeOfDay, timezoneOffsetHours) : 'morning'
    
    // Check if pro has availability for this adjusted preference
    const availKey = `${dayType}${adjustedTimeOfDay.charAt(0).toUpperCase() + adjustedTimeOfDay.slice(1)}`
    
    if (proSchedule[availKey] === true) {
      // Give lower score if therapist has "all days available" (likely default data)
      score += hasAllSlotsAvailable ? 2 : 5
    }
  })
  
  return score
}

/**
 * Get timezone offset difference in hours
 */
function getTimezoneOffsetHours(userTimezone: string, proTimezone: string | null): number {
  if (!proTimezone) return 0
  
  // Map IANA timezones to UTC offsets (simplified for Canadian timezones)
  const timezoneOffsets: { [key: string]: number } = {
    'America/Vancouver': -7,   // PT (permanent DST since 2026-03-08)
    'America/Edmonton': -6,    // MT (permanent DST since 2026-11-01)
    'America/Regina': -6,      // CST (no DST)
    'America/Winnipeg': -6,    // CST
    'America/Toronto': -5,     // EST
    'America/Halifax': -4,     // AST
    'America/St_Johns': -3.5   // NST
  }
  
  const userOffset = timezoneOffsets[userTimezone] || -5
  const proOffset = timezoneOffsets[proTimezone] || -5
  
  return proOffset - userOffset
}

/**
 * Adjust time of day based on timezone offset
 */
function adjustTimeOfDay(timeOfDay: string, offsetHours: number): string {
  // Map time periods to hour ranges
  const timePeriods = {
    morning: { start: 6, end: 12 },
    afternoon: { start: 12, end: 17 },
    evening: { start: 17, end: 23 }
  }
  
  const period = timePeriods[timeOfDay as keyof typeof timePeriods]
  if (!period) return timeOfDay
  
  // Adjust the time range by offset
  const adjustedStart = period.start + offsetHours
  
  // Determine which time period the adjusted time falls into
  if (adjustedStart >= 6 && adjustedStart < 12) return 'morning'
  if (adjustedStart >= 12 && adjustedStart < 17) return 'afternoon'
  if (adjustedStart >= 17 && adjustedStart < 23) return 'evening'
  
  // Handle edge cases (very early or very late)
  if (adjustedStart < 6) return 'evening' // Previous day's evening
  if (adjustedStart >= 23) return 'morning' // Next day's morning
  
  return timeOfDay
}

/**
 * Find and persist matches for a freshly-inserted matching_quizzes row.
 * Used by both authenticated submit (saveQuizData) and guest submit (/api/quiz/temp).
 *
 * When `opts.includeBesFallback` is set, under-4 internal matches are padded
 * with BES pros and bes_referrals rows are persisted with source=ALLI_MATCH.
 * Client identity for the BES rows is derived from the quiz row + optional
 * `client` field (guest firstName/email) or a users lookup (authenticated).
 */
export async function runMatching(
  quiz: {
    id: string
    type: string | null
    tier: string | null
    rate: number | null
    timezone: string | null
    times: string[]
    insurance: string[]
    concerns?: string[]
    province?: string | null
    userId?: string | null
    firstName?: string | null
    email?: string | null
  },
  supabase?: SupabaseClient,
  opts: FindMatchingProsOptions = {},
): Promise<{ proIds: string[]; besProIds: string[]; error?: string }> {
  const client = supabase || createClient()

  if (!quiz.type || !quiz.tier || quiz.rate == null || !quiz.timezone) {
    return { proIds: [], besProIds: [], error: 'Quiz missing required matching fields' }
  }

  const criteria: MatchingCriteria = {
    sessionType: quiz.type as 'individual' | 'couples',
    therapistTier: quiz.tier as 'resident' | 'associate',
    rate: quiz.rate,
    timezone: quiz.timezone,
    availability: quiz.times,
    insurance: quiz.insurance,
    province: quiz.province ?? undefined,
  }

  const { proIds, besProIds, besMatchReasons, error: matchingError } = await findMatchingPros(
    criteria,
    client,
    opts,
  )

  if (matchingError && proIds.length === 0 && besProIds.length === 0) {
    return { proIds: [], besProIds: [], error: matchingError }
  }

  if (proIds.length > 0) {
    await updateMatchingQuizWithPros(quiz.id, proIds, client)
  }

  if (besProIds.length > 0) {
    await updateMatchingQuizWithBesPros(quiz.id, besProIds, besMatchReasons, client)
  }

  return { proIds, besProIds }
}

/**
 * Update matching quiz with matched pro IDs
 */
export async function updateMatchingQuizWithPros(
  quizId: string,
  proIds: string[],
  supabase?: SupabaseClient
): Promise<{ success: boolean; error?: string }> {
  const client = supabase || createClient()
  
  try {
    const { error } = await client
      .from('matching_quizzes')
      .update({
        matchedProIds: proIds,
        status: 'matched',
        updatedAt: new Date().toISOString()
      })
      .eq('id', quizId)
    
    if (error) {
      console.error('Error updating matching quiz:', error)
      return { success: false, error: 'Failed to update matching results' }
    }
    
    // Increment matched count for all matched pros
    for (const proId of proIds) {
      // First get current matched count
      const { data: proData, error: fetchError } = await client
        .from('pros')
        .select('matched')
        .eq('id', proId)
        .single()
      
      if (fetchError) {
        console.error(`Error fetching matched count for pro ${proId}:`, fetchError)
        continue
      }
      
      // Increment the matched count
      const currentCount = proData?.matched || 0
      const { error: updateError } = await client
        .from('pros')
        .update({ matched: currentCount + 1 })
        .eq('id', proId)
      
      if (updateError) {
        console.error(`Error updating matched count for pro ${proId}:`, updateError)
      }
    }
    
    return { success: true }
    
  } catch (error) {
    console.error('Unexpected error updating matching quiz:', error)
    return { success: false, error: 'An unexpected error occurred' }
  }
}